import type { AutorDaTratativa, PersonaDaTratativa, SituacaoDaTratativa } from './contrato';

/**
 * Como a situação de uma tratativa se mostra — cor e texto.
 *
 * Vive fora da grade e fora do detalhe porque as duas telas mostram a mesma
 * coisa: a grade resume a conversa, o detalhe a abre. Duas repetições já pedem
 * extração (CLAUDE.md, "Regras de interface", item 1), e situação com cor
 * diferente em cada tela é o tipo de divergência que ninguém percebe até a
 * demonstração.
 */

/** O tom de cada situação. `destructive` em `ABERTO` é do protótipo: pendência sem resposta é o que atrasa o fechamento. */
export const SITUACAO_VARIANTE: Record<
  SituacaoDaTratativa,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  ABERTO: 'destructive',
  RESPONDIDO: 'default',
  RESOLVIDO: 'secondary',
};

/** Papel do autor reduzido às duas personas que conversam: SME e DRE. */
export function personaDoAutor(autorPapel: AutorDaTratativa): PersonaDaTratativa {
  return autorPapel === 'DRE' ? 'DRE' : 'SME';
}

/**
 * Como a situação se chama para quem está lendo.
 *
 * O mesmo `ABERTO` é "Aguardando DRE" para a SME e "Aberto" para a DRE: dizer
 * "aguardando DRE" a quem É a DRE não informa de quem é a vez. Regra do
 * protótipo, mantida — é ela que faz a bandeja responder "o que eu preciso
 * fazer hoje" em vez de só listar registros.
 */
export function rotuloDaSituacao(
  situacao: SituacaoDaTratativa,
  autorPapel: AutorDaTratativa,
  persona: PersonaDaTratativa,
): string {
  if (situacao === 'RESOLVIDO') {
    return 'Resolvido';
  }

  const souAutor = personaDoAutor(autorPapel) === persona;

  if (situacao === 'ABERTO') {
    if (souAutor) {
      return persona === 'SME' ? 'Aguardando DRE' : 'Aguardando SME';
    }
    return 'Aberto';
  }

  if (souAutor) {
    return 'Respondido — sua vez';
  }
  return persona === 'SME' ? 'Respondido (aguardando SME)' : 'Respondido (aguardando DRE)';
}
